/**
 * Wake Lock — keep the iPad screen alive during exhibition use.
 *
 * The Wake Lock API requires a user gesture for the first request, so
 * `requestWakeLock` must be called from a pointerdown / click handler.
 * Subsequent re-requests (e.g. after the page returns from background)
 * don't strictly need a fresh gesture, but most browsers will silently
 * ignore them if visibility is `hidden` — the visibilitychange listener
 * below covers the common "user backgrounded the app and came back" case.
 *
 * On unsupported browsers this is a no-op. Failures are non-fatal — the
 * piece keeps running, the iPad will just dim/sleep on the OS schedule.
 */

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

interface NavigatorWithWakeLock {
  wakeLock?: {
    request(type: 'screen'): Promise<WakeLockSentinelLike>;
  };
}

let sentinel: WakeLockSentinelLike | null = null;
let listenerAttached = false;

function getNavigatorWakeLock(): NavigatorWithWakeLock['wakeLock'] | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & NavigatorWithWakeLock).wakeLock;
}

/**
 * Request a screen wake lock. Idempotent — subsequent calls while a
 * sentinel is already held are no-ops. Safe to invoke from any user
 * gesture path; safe to invoke on browsers without the API.
 */
export async function requestWakeLock(): Promise<void> {
  const api = getNavigatorWakeLock();
  if (!api) return;
  if (sentinel !== null && !sentinel.released) return;
  try {
    const next = await api.request('screen');
    sentinel = next;
    next.addEventListener('release', () => {
      // The OS may yank the lock when the page is backgrounded; clear
      // the cached sentinel so the visibilitychange path can re-acquire.
      if (sentinel === next) sentinel = null;
    });
  } catch (err) {
    // Permission denied / unsupported — non-fatal.
    // eslint-disable-next-line no-console
    console.warn('[wake-lock] request failed:', err);
  }
}

/**
 * Wire up a visibilitychange listener that re-requests the wake lock
 * when the page returns to the foreground. Idempotent — safe to call
 * more than once. Call this once at App.start().
 */
export function installWakeLockVisibilityHandler(): void {
  if (listenerAttached) return;
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && sentinel === null) {
      void requestWakeLock();
    }
  });
  listenerAttached = true;
}
